import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { usePlatformStore } from '../platform/session'
import type {
  SceneObject,
  SceneData,
  SceneTab,
  SceneConfig,
  FloorDef,
  Connection,
  ObjectGroup,
  RealtimeDebugConfig,
  DataSourceMode,
  ProjectMeta,
  ProjectRealtimeConfig,
  ProjectFileRef,
  PathDef,
  EditorSnapshot,
  PhysicsConfig,
  PhysicsEditSelection,
  PhysicsZone,
  PhysicsFlowEdge,
  PhysicsSource,
  PhysicsBinding,
  RuntimePoliciesConfig,
} from '../types/scene'
import type { DataIntegrationPreviewConfig, ObjectDataBindings } from '../types/dataBindings'
import type { SourceShape } from '../types/sourceAdapter'
import type {
  ConnectDataSecrets,
  DataService,
  ProjectConnectConfig,
  ProjectConnectStatus,
  RuntimeSessionAuth,
} from '../types/connectData'
import { DEFAULT_LIGHTING, FLAT_LIGHTING } from '../types/scene'
import { serialize, deserialize, hydrateSceneData, stripRealtimeCredentials, defaultProjectConnectConfig } from '@/utils/serializer'
import { CURRENT_SCENE_VERSION, migrateLegacyPolylineObject } from '@/utils/migrate'
import type { ProjectData } from '@/utils/serializer'
import { setByPath } from '@/utils/liveDataPaths'
import { cloneSceneObjectAt } from '@/utils/cloneSceneObject'
import { activePathsOf, addPathToScene, removePathFromScene, updatePathInScene } from './scenePathOps'
import { useEditorPrefsStore } from './editorPrefs'
import { deepClone } from '@/utils/deepClone'
import {
  applyProjectSecrets,
  clearConnectDataSecrets,
  clearConnectDataToken,
  loadConnectDataSecrets,
  loadProjectSecrets,
  pickRealtimeSecrets,
  saveConnectDataSecrets,
  saveProjectSecrets,
} from '@/utils/localSecrets'
import {
  dataServicesOf,
  upsertDataService as upsertDataServiceInScene,
  removeDataService as removeDataServiceFromScene,
} from '@/connect-data/dataServices'
import { connectAuthProfileKey } from '@/connect-data/connectAuthProfile'
import { useSelectionStore } from './selection'

// 云端优先（docs/10）：项目数据只活在服务端，本机不再保留任何可当作「项目进度」的草稿。

/** 一个工程至少有一个场景，删空/新建工程都用这个默认名，避免出现两个不同的"默认场景名" */
const DEFAULT_SCENE_NAME = '厂区总览'

const DEFAULT_PROJECT_REALTIME_CONFIG = (): ProjectRealtimeConfig => ({
  transport: 'websocket',
  endpoint: '',
  token: '',
  useMock: false,
  batchMs: 300,
  pollMs: 3000,
  dataSourceMode: 'mock',
  auditUrl: '',
  simulationEnabled: false,
  simulationEndpoint: '',
  simulationPollMs: 3000,
})

function cloneSceneConfig(config: SceneConfig | null | undefined): SceneConfig | null {
  return config ? deepClone(config) : null
}

function cloneSourceShape(t: SourceShape | undefined | null): SourceShape | null {
  return t ? deepClone(t) : null
}

function cloneDataServices(t: DataService[] | undefined | null): DataService[] | null {
  return Array.isArray(t) && t.length > 0 ? deepClone(t) : null
}

/**
 * 旧草稿/旧撤销栈兼容（docs/05-连接数据.md §5）：快照里旧键 `sourceAdapterTemplate` → 新键 `sourceShape`。
 * 只搬不并存：此后一律用新键。
 */
function normalizeSnapshotSourceShape(
  snap: (EditorSnapshot & { sourceAdapterTemplate?: SourceShape | null }) | null,
): EditorSnapshot | null {
  if (!snap) return null
  const legacy = snap.sourceAdapterTemplate
  if (legacy === undefined) return snap
  const { sourceAdapterTemplate: _omit, ...rest } = snap
  return { ...rest, sourceShape: snap.sourceShape ?? legacy ?? null }
}

/** 快照是否已带源形态字段（新键或旧键都算），用于旧草稿回填判定 */
function snapshotHasSourceShape(snap: EditorSnapshot | null | undefined): boolean {
  if (!snap) return false
  return 'sourceShape' in snap || 'sourceAdapterTemplate' in snap
}

/**
 * 深拷贝场景数据。模板（TemplateDialog.vue 里 `import xxxData from '../templates/xxx.json'`）
 * 在整个页面生命周期内是同一个模块级常量对象；若直接把它赋给 tab.data，之后任何一次
 * updateObjectParams/updateObject（内部是 `scene.objects[idx] = {...}`，只替换数组元素，
 * 不替换 scene.objects 这个数组本身的引用）都会原地改到这个共享数组上——等于把用户在编辑器里
 * 对某个对象的调整（比如关掉屋顶显示）永久写回了模板常量。后续同一个模板再"加载"多少次，
 * 拿到的都是被污染过的旧数据，而不是模板文件本来的内容。这里统一深拷贝一份，
 * 保证进入 tab.data 的对象树和外部（模板常量 / 反序列化产物）完全不共享引用。
 */
function cloneSceneData(data: SceneData): SceneData {
  return deepClone(data)
}

function clonePhysicsForSnapshot(pc: PhysicsConfig | undefined): PhysicsConfig | null | undefined {
  if (pc === undefined) return undefined
  if (pc == null) return null
  return deepClone(pc)
}

const DEFAULT_SCENE_DATA = (): SceneData => ({
  version: CURRENT_SCENE_VERSION,
  meta: { id: uuidv4(), name: '新场景', createdAt: new Date().toISOString() },
  scene: {
    background: '#d0e8f0',
    ground: { size: 300, color: '#a8b890' },
    camera: { position: [60, 60, -60], target: [0, 0, 0] },
    lighting: { ...DEFAULT_LIGHTING },
    gridSize: 1,
    showCompass: true,
    showMinimap: true,
  },
  objects: [],
  floors: [{ id: 'f1', name: 'F1 一层', elevation: 0, height: 5 }],
  connections: [],
  groups: [],
  paths: [],
})

export const useSceneStore = defineStore('scene', () => {
  const editorPrefsStore = useEditorPrefsStore()

  const tabs = ref<SceneTab[]>([])
  const activeTabId = ref<string>('')
  const lastSavedTime = ref<string>('')
  /**
   * 工程元信息（id/名称/创建时间）。云项目场景里 scene.json 的 meta 与 project 表 name 是两处：
   * meta.name 是「最近一次保存时的工程名」，project.name 是列表显示名（保存时若不一致则改名）。
   */
  const projectMeta = ref<ProjectMeta>({ id: uuidv4(), name: '未命名工程', createdAt: new Date().toISOString() })
  /** 工程级共享的实时数据连接配置（不含 showBadge/preferPerformance/enabled） */
  const projectRealtimeConfig = ref<ProjectRealtimeConfig>(DEFAULT_PROJECT_REALTIME_CONFIG())
  /** 最近一次正式保存工程时的连接配置快照，用于 dirty 判定 */
  const lastSavedRealtimeConfig = ref<ProjectRealtimeConfig>(DEFAULT_PROJECT_REALTIME_CONFIG())
  /** 工程级「项目连接」非密钥配置（baseUrl / username / organizationId / pollIntervalMs，进 .f3p 的 project.connectData） */
  const projectConnectConfig = ref<ProjectConnectConfig>(defaultProjectConnectConfig())
  /** 最近一次正式保存时的项目连接配置快照，用于 dirty 判定 */
  const lastSavedConnectConfig = ref<ProjectConnectConfig>(defaultProjectConnectConfig())
  /**
   * 工程级「项目连接」本机密钥（token/密码/过期时间）的内存镜像。
   * 与 localSecrets 同步；UI 必须读此 ref，不能每次直读 localStorage（非响应式会导致切换面板后状态「丢失」）。
   */
  const connectDataSecrets = ref<ConnectDataSecrets>({})
  /** 工程文件来源与保存目标（.f3p），保存粒度是整个工程，不再挂在单个 tab 上 */
  const projectFileRef = ref<ProjectFileRef | null>(null)
  /** 调试悬浮态是否开启：纯会话态，从不持久化；冷启动固定为 false，但切场景不重置（相对旧行为的改进：旧版这个字段挂在场景数据上，切 tab 会跟着变） */
  const debugEnabled = ref(false)
  /** Dashboard 组态模式：画布上叠加可编辑面板，与预览共用 Mock 管道时可读合并后的 liveData */
  const dashboardComposeMode = ref(false)
  /** 组态：当前选中的 Dashboard 控件 id（右侧「控件」Tab 与画布选中同步） */
  const composeSelectedWidgetId = ref<string | null>(null)
  /** 右侧面板 Tab：普通模式 scene/physics；组态模式额外支持 widget */
  const composeRightPanelTab = ref<'scene' | 'physics' | 'widget'>('scene')
  /** 右侧面板是否收起：收起后完全不占画布空间，只留 SceneTabs 那一条的切换按钮 */
  const rightPanelCollapsed = ref(false)
  /** 组态：Dashboard 面板栅格的像素尺寸（由 DashboardComposeShell ResizeObserver 写入） */
  const composeGridSize = ref({ w: 0, h: 0 })
  /** 组态：是否将移动/缩放/插入对齐到 1/12 栅格（关则为像素级自由布局） */
  const composeSnapToGrid = ref(false)
  /** 组态：选中控件为「自动字号」时，由画布 DOM 测得的实际像素（供属性面板展示） */
  const composeAutoResolvedFontPx = ref<number | null>(null)
  /** 组态：标题/标签为「自动」时测得的像素 */
  const composeAutoResolvedLabelFontPx = ref<number | null>(null)
  /** 编辑器预览/组态：realtimePipeline 每次合并后的 liveData，供 Dashboard 切片 */
  const editorPreviewMergedLiveData = ref<Record<string, unknown>>({})
  /**
   * 最近一次「真实数据」轮询合并结果（不含 Mock）。
   * Mock 以此为种子抖动，避免凭空 Math.random；切 mock/停管线时不清空。
   */
  const lastLiveDataSeed = ref<Record<string, unknown>>({})
  /** 编辑器：预览态下覆盖 liveData 路径（与 Mock 合并后参与 computeLiveDataPatches） */
  const previewLiveDataOverrides = ref<Record<string, unknown>>({})
  /** 模式：运行数据观测（独立页 simulation-monitor.html 内为 true；主编辑器内不再切换此态） */
  const simulationMonitorMode = ref(false)
  /** 左侧栏 Tab：组件 / 场景树 / 仿真 */
  const editorLeftTab = ref<'components' | 'sceneTree' | 'physics'>('components')
  /** 仿真资源在右侧详情区的当前选中（与 3D 对象选中互斥） */
  const physicsEditSelection = ref<PhysicsEditSelection | null>(null)

  // 当前激活的场景数据
  function activeScene(): SceneData | undefined {
    return tabs.value.find(t => t.id === activeTabId.value)?.data
  }

  function activeObjects(): SceneObject[] {
    return activeScene()?.objects ?? []
  }

  function reloadConnectDataSecrets(projectId = projectMeta.value.id) {
    connectDataSecrets.value = { ...(loadConnectDataSecrets(projectId) ?? {}) }
  }

  watch(
    () => projectMeta.value.id,
    (projectId) => {
      reloadConnectDataSecrets(projectId)
    },
  )

  function persistLocalSecrets() {
    // 保留 connectData 段（项目连接密码/token 与本机实时密钥互不覆盖）
    const existing = loadProjectSecrets(projectMeta.value.id)
    saveProjectSecrets(projectMeta.value.id, {
      ...pickRealtimeSecrets(projectRealtimeConfig.value),
      connectData: existing.connectData,
    })
  }

  /** 组装完整工程草稿对象（云项目 scene.json 的唯一序列化来源；不落本地） */
  function buildDraftObject(): ProjectDraft {
    return {
      meta: projectMeta.value,
      tabs: tabs.value,
      activeTabId: activeTabId.value,
      realtimeDebugConfig: stripRealtimeCredentials(projectRealtimeConfig.value),
      lastSavedRealtimeConfig: stripRealtimeCredentials(lastSavedRealtimeConfig.value),
      projectConnectConfig: projectConnectConfig.value,
      lastSavedConnectConfig: lastSavedConnectConfig.value,
      projectFileRef: projectFileRef.value,
    }
  }

  /**
   * 内存脏标记（docs/10 §2.3）：任何编辑操作只置此标记，不做任何存储写入；
   * App.vue 的 autosave 节流（防抖 2s + 限流 10s）监听此信号发起云端保存。
   */
  const dirtySignal = ref(0)
  function markDirty(): void {
    dirtySignal.value++
  }

  // docs/10 §2.3：对象级编辑（addObject/updateObject/拖拽/撤销等不走显式 markDirty 的操作）
  // 由深度监听统一置脏——内存即数据源，App.vue 的 autosave 节流据此上云。
  watch(tabs, () => markDirty(), { deep: true })

  interface ProjectDraft {
    meta: ProjectMeta
    tabs: SceneTab[]
    activeTabId: string
    realtimeDebugConfig: ProjectRealtimeConfig
    lastSavedRealtimeConfig: ProjectRealtimeConfig
    projectConnectConfig?: ProjectConnectConfig
    lastSavedConnectConfig?: ProjectConnectConfig
    projectFileRef: ProjectFileRef | null
  }

  function startFreshProject() {
    clearPreviewLiveDataOverrides()
    projectMeta.value = { id: uuidv4(), name: '未命名工程', createdAt: new Date().toISOString() }
    projectRealtimeConfig.value = DEFAULT_PROJECT_REALTIME_CONFIG()
    lastSavedRealtimeConfig.value = DEFAULT_PROJECT_REALTIME_CONFIG()
    projectConnectConfig.value = defaultProjectConnectConfig()
    lastSavedConnectConfig.value = defaultProjectConnectConfig()
    connectDataSecrets.value = {}
    projectFileRef.value = null
    debugEnabled.value = false
    dashboardComposeMode.value = false
    resetDashboardComposeUi()
    simulationMonitorMode.value = false
    editorLeftTab.value = 'components'
    physicsEditSelection.value = null
    editorPreviewMergedLiveData.value = {}
    lastLiveDataSeed.value = {}
    tabs.value = []
    const newTab = createTab(DEFAULT_SCENE_NAME)
    activeTabId.value = newTab.id
    useSelectionStore().resetForFreshProject()
  }

  /** 从草稿对象恢复整个工程（本地草稿与云端 scene.json 共用同一恢复路径，P1） */
  function restoreFromDraft(parsed: ProjectDraft): boolean {
    try {
      // 旧草稿兼容：lastSavedSourceAdapterTemplate / lastSavedSnapshot.sourceAdapterTemplate → 新键 sourceShape
      tabs.value = parsed.tabs.map((rawTab) => {
        const legacyLastSaved = (rawTab as SceneTab & { lastSavedSourceAdapterTemplate?: SourceShape | null })
          .lastSavedSourceAdapterTemplate
        const snapshot = normalizeSnapshotSourceShape(
          (rawTab.lastSavedSnapshot as EditorSnapshot & { sourceAdapterTemplate?: SourceShape | null }) ?? null,
        )
        return {
          ...rawTab,
          data: hydrateSceneData({ ...DEFAULT_SCENE_DATA(), ...rawTab.data }),
          lastSavedSnapshot: snapshot,
          lastSavedSceneConfig: rawTab.lastSavedSceneConfig ?? null,
          lastSavedSourceShape: legacyLastSaved ?? undefined,
        } as SceneTab
      })
      for (const tab of tabs.value) {
        if (!tab.lastSavedSnapshot) {
          tab.lastSavedSnapshot = {
            objects: deepClone(tab.data.objects ?? []),
            connections: deepClone(tab.data.connections ?? []),
            floors: deepClone(tab.data.floors ?? []),
            groups: deepClone(tab.data.groups ?? []),
            paths: deepClone(tab.data.paths ?? []),
            physicsConfig: clonePhysicsForSnapshot(tab.data.physicsConfig),
            sourceShape: cloneSourceShape(tab.data.sourceShape),
            dataServices: cloneDataServices(tab.data.dataServices),
          }
        } else if (!snapshotHasSourceShape(tab.lastSavedSnapshot)) {
          // 旧草稿快照无此字段：用已保存基准或当前场景回填，避免升级后误标 dirty
          tab.lastSavedSnapshot = {
            ...tab.lastSavedSnapshot,
            sourceShape:
              tab.lastSavedSourceShape !== undefined
                ? cloneSourceShape(tab.lastSavedSourceShape)
                : cloneSourceShape(tab.data.sourceShape),
          }
        }
        if (!tab.lastSavedSceneConfig) {
          tab.lastSavedSceneConfig = cloneSceneConfig(tab.data.scene)
        }
        if (tab.lastSavedSourceShape === undefined) {
          tab.lastSavedSourceShape = cloneSourceShape(
            tab.lastSavedSnapshot.sourceShape ?? tab.data.sourceShape,
          )
        }
      }
      projectMeta.value = parsed.meta ?? { id: uuidv4(), name: '未命名工程', createdAt: new Date().toISOString() }
      const draftCfg = parsed.realtimeDebugConfig ?? {}
      const migratedSecrets = pickRealtimeSecrets(draftCfg)
      if (migratedSecrets.token || migratedSecrets.simulationToken) {
        saveProjectSecrets(projectMeta.value.id, {
          ...loadProjectSecrets(projectMeta.value.id),
          ...migratedSecrets,
        })
      }
      const restored = applyProjectSecrets(
        { ...DEFAULT_PROJECT_REALTIME_CONFIG(), ...stripRealtimeCredentials(draftCfg) },
        loadProjectSecrets(projectMeta.value.id),
      )
      projectRealtimeConfig.value = restored
      lastSavedRealtimeConfig.value = applyProjectSecrets(
        { ...DEFAULT_PROJECT_REALTIME_CONFIG(), ...stripRealtimeCredentials(parsed.lastSavedRealtimeConfig ?? draftCfg) },
        loadProjectSecrets(projectMeta.value.id),
      )
      persistLocalSecrets()
      // 工程级「项目连接」非密钥配置：草稿里没有则回退默认（连接数据登录仍可恢复——token 在 localSecrets）
      projectConnectConfig.value = parsed.projectConnectConfig ?? defaultProjectConnectConfig()
      lastSavedConnectConfig.value = parsed.lastSavedConnectConfig ?? { ...projectConnectConfig.value }
      reloadConnectDataSecrets(projectMeta.value.id)
      normalizeDataSourceMode()
      projectFileRef.value = parsed.projectFileRef ?? null
      activeTabId.value = parsed.activeTabId
      debugEnabled.value = false // 冷启动时始终禁用实时调试，不自动恢复上次调试状态
      return true
    } catch (err) {
      // 草稿损坏/版本不兼容：丢弃并回退为新工程；保留 debug 日志便于排查「草稿无故丢失」
      console.debug('[scene] 工程草稿恢复失败，已回退为新工程', err)
      return false
    }
  }

  /**
   * 云项目加载（docs/10 §2.2）：从云端拉 head 版本场景恢复工程。
   * 返回 'loaded'（恢复成功）/ 'empty'（云端新项目无 head，开全新空白场景）/ 'error'（拉取失败）。
   * 关键：空云端项目绝不能再回退到任何本机残留内容（否则新建项目会显示上一个工程的内容）。
   */
  async function loadCloudProject(publicId: string): Promise<'loaded' | 'empty' | 'error'> {
    const { cloudLoadProject } = await import('../platform/projectSync')
    let draft: Record<string, unknown> | null
    try {
      draft = await cloudLoadProject(publicId)
    } catch {
      return 'error' // 网络/服务异常
    }
    if (!draft) {
      // 云端新项目（无 head 版本）→ 全新空白场景
      startFreshProject()
      return 'empty'
    }
    return restoreFromDraft(draft as unknown as ProjectDraft) ? 'loaded' : 'error'
  }

  /**
   * 云保存串行队列：autosave 节流 / Ctrl+S / 关闭场景 / 套模板等多处都会各自调用 save()，
   * 一旦有两次云保存同时在飞（比如网络慢时又赶上下一次 autosave 触发），它们会用同一个旧的
   * baseVersionSeq 各发一次请求——先到的服务端接受，后到的就会被判成「乐观锁冲突」，
   * 误报成「这台电脑之外有人保存过」，其实只是自己这一个标签页的重复请求撞车。
   * 用队列把所有云保存严格排成一条线：后一次一定等前一次完全结束（baseVersionSeq 已经推进）后才发出去。
   */
  let saveChain: Promise<void> = Promise.resolve()
  function enqueueSave(run: () => Promise<void>): Promise<void> {
    const next = saveChain.then(run, run)
    saveChain = next
    return next
  }

  /** 冲突解决「强制保存」：不校验乐观锁，直接把当前内容覆盖为云端新版本 */
  async function forceSaveToCloud(): Promise<void> {
    return enqueueSave(async () => {
      const platform = usePlatformStore()
      if (!platform.currentProject) return
      const { cloudSaveDraft } = await import('../platform/projectSync')
      await cloudSaveDraft(platform, buildDraftObject(), { force: true, manual: true })
      if (platform.saveState === 'saved') {
        markProjectSaved()
        touchLastSavedAt()
      }
    })
  }

  /**
   * 重命名工程（保存/另存为时用户起名）：改内存元信息并标记脏，由 autosave 上云；
   * 云项目列表显示名由保存链路同步到 project 表（见 saveToCloud）。
   */
  function renameProject(name: string) {
    projectMeta.value = { ...projectMeta.value, name }
    markDirty()
  }

  /**
   * 用服务端真实项目名对齐本地缓存（打开项目时用）：不标脏、不触发 autosave。
   * 场景：新建项目时草稿还是占位名「未命名工程」；或项目在列表页被改名后重新打开编辑器——
   * 这两种情况本地缓存的 projectMeta.name 都可能与服务端 project.name 不一致，
   * 若不对齐，首次 save() 会把这个不一致误判为「用户改名」，反过来把服务端名覆盖掉。
   */
  function syncProjectName(name: string) {
    if (name && name !== projectMeta.value.name) {
      projectMeta.value = { ...projectMeta.value, name }
    }
  }

  /** 打开一个工程后整体替换工作区；载入的内容视为已保存（干净状态）。fileRef 仅历史 .f3p 流程遗留，云端加载传 null */
  function loadProject(project: ProjectData, fileRef: ProjectFileRef | null) {
    clearPreviewLiveDataOverrides()
    projectMeta.value = project.meta
    const fromFile = pickRealtimeSecrets(project.realtimeDebugConfig)
    const existing = loadProjectSecrets(project.meta.id)
    saveProjectSecrets(project.meta.id, {
      token: existing.token || fromFile.token,
      simulationToken: existing.simulationToken || fromFile.simulationToken,
      // 项目连接密钥（connectData 段）按工程 id 保留，不被打开文件冲掉
      connectData: existing.connectData,
    })
    projectRealtimeConfig.value = applyProjectSecrets(
      { ...DEFAULT_PROJECT_REALTIME_CONFIG(), ...stripRealtimeCredentials(project.realtimeDebugConfig) },
      loadProjectSecrets(project.meta.id),
    )
    lastSavedRealtimeConfig.value = { ...projectRealtimeConfig.value }
    // 工程级「项目连接」：非密钥从 .f3p 读，密码/token 从本机 localSecrets（按工程 id）读，互不覆盖
    projectConnectConfig.value = project.connectData ?? defaultProjectConnectConfig()
    lastSavedConnectConfig.value = { ...projectConnectConfig.value }
    reloadConnectDataSecrets(project.meta.id)
    projectFileRef.value = fileRef
    tabs.value = project.scenes.map((s) => {
      const data = hydrateSceneData({ ...DEFAULT_SCENE_DATA(), ...s.data })
      const tab: SceneTab = {
        id: s.id,
        name: s.name,
        data,
        lastSavedSnapshot: {
          objects: deepClone(data.objects ?? []),
          connections: deepClone(data.connections ?? []),
          floors: deepClone(data.floors ?? []),
          groups: deepClone(data.groups ?? []),
          paths: deepClone(data.paths ?? []),
          physicsConfig: clonePhysicsForSnapshot(data.physicsConfig),
          sourceShape: cloneSourceShape(data.sourceShape),
          dataServices: cloneDataServices(data.dataServices),
        },
        lastSavedSceneConfig: cloneSceneConfig(data.scene),
        lastSavedSourceShape: cloneSourceShape(data.sourceShape),
      }
      return tab
    })
    activeTabId.value = tabs.value.some(t => t.id === project.activeSceneId)
      ? project.activeSceneId
      : (tabs.value[0]?.id ?? '')
    debugEnabled.value = false
    normalizeDataSourceMode()
  }

  function createTab(name: string): SceneTab {
    const tab: SceneTab = {
      id: uuidv4(),
      name,
      data: DEFAULT_SCENE_DATA(),
      lastSavedSnapshot: null,
      lastSavedSceneConfig: null,
      lastSavedSourceShape: null,
    }
    tab.data.meta.name = name
    tabs.value.push(tab)
    tab.lastSavedSnapshot = getTabSnapshot(tab.id)
    tab.lastSavedSceneConfig = cloneSceneConfig(tab.data.scene)
    tab.lastSavedSourceShape = cloneSourceShape(tab.data.sourceShape)
    return tab
  }

  function resetDashboardComposeUi() {
    composeSelectedWidgetId.value = null
    composeRightPanelTab.value = 'scene'
    composeGridSize.value = { w: 0, h: 0 }
    composeSnapToGrid.value = false
    composeAutoResolvedFontPx.value = null
    composeAutoResolvedLabelFontPx.value = null
  }

  function setComposeSnapToGrid(on: boolean) {
    composeSnapToGrid.value = on
  }

  function setComposeGridSize(size: { w: number; h: number }) {
    composeGridSize.value = size
  }

  function setComposeAutoResolvedFontPx(px: number | null) {
    composeAutoResolvedFontPx.value = px
  }

  function setComposeAutoResolvedLabelFontPx(px: number | null) {
    composeAutoResolvedLabelFontPx.value = px
  }

  function switchTab(id: string) {
    clearPreviewLiveDataOverrides()
    dashboardComposeMode.value = false
    resetDashboardComposeUi()
    activeTabId.value = id
    markDirty()
  }

  function getTabById(id: string) {
    return tabs.value.find(t => t.id === id)
  }

  function getTabSnapshot(id: string): EditorSnapshot {
    const tab = getTabById(id)
    if (!tab) {
      return {
        objects: [],
        connections: [],
        floors: [],
        groups: [],
        paths: [],
        sourceShape: null,
        dataServices: null,
      }
    }
    return {
      objects: deepClone(tab.data.objects ?? []),
      connections: deepClone(tab.data.connections ?? []),
      floors: deepClone(tab.data.floors ?? []),
      groups: deepClone(tab.data.groups ?? []),
      paths: deepClone(tab.data.paths ?? []),
      physicsConfig: clonePhysicsForSnapshot(tab.data.physicsConfig),
      sourceShape: cloneSourceShape(tab.data.sourceShape),
      dataServices: cloneDataServices(tab.data.dataServices),
    }
  }

  function snapshotKey(snapshot: EditorSnapshot | null | undefined): string {
    if (!snapshot) return ''
    return JSON.stringify(snapshot)
  }

  /**
   * 是否与最近一次正式保存相比有改动。
   * EditorSnapshot（含物体与 sourceShape）走撤销栈；地坪/光照等 SceneConfig 仍只进 dirty、不进撤销。
   * lastSavedSourceShape 与快照内字段双写，兼容升级前草稿。
   */
  function isTabDirty(id: string): boolean {
    const tab = getTabById(id)
    if (!tab) return false
    if (!tab.lastSavedSnapshot) return true
    if (snapshotKey(getTabSnapshot(id)) !== snapshotKey(tab.lastSavedSnapshot)) return true
    if (JSON.stringify(tab.data.scene ?? null) !== JSON.stringify(tab.lastSavedSceneConfig ?? null)) return true
    if (JSON.stringify(tab.data.sourceShape ?? null)
      !== JSON.stringify(tab.lastSavedSourceShape ?? null)) return true
    // v2：数据服务列表（sourceShape 挂服务上，服务增减/改名要能点亮保存）
    return JSON.stringify(tab.data.dataServices ?? null)
      !== JSON.stringify(tab.lastSavedSnapshot?.dataServices ?? null)
  }

  /** 是否与最近一次正式保存工程相比有改动：任一场景 dirty，或工程级连接配置有变 */
  function isProjectRealtimeConfigDirty(): boolean {
    return JSON.stringify(stripRealtimeCredentials(projectRealtimeConfig.value))
      !== JSON.stringify(stripRealtimeCredentials(lastSavedRealtimeConfig.value))
  }

  /** 工程级「项目连接」非密钥配置是否有改动（不比较密钥——密钥只进本机） */
  function isProjectConnectConfigDirty(): boolean {
    return JSON.stringify(projectConnectConfig.value)
      !== JSON.stringify(lastSavedConnectConfig.value)
  }

  function isProjectDirty(): boolean {
    return tabs.value.some(t => isTabDirty(t.id)) || isProjectRealtimeConfigDirty() || isProjectConnectConfigDirty()
  }

  /** 兼容旧逻辑：是否为空/无内容（不代表未保存） */
  function isTabUnchanged(id: string): boolean {
    const tab = tabs.value.find(t => t.id === id)
    if (!tab?.data) return true
    const d = tab.data
    const objs = d.objects ?? []
    const conns = d.connections ?? []
    const grps = d.groups ?? []
    const paths = d.paths ?? []
    const floors = d.floors ?? []
    if (objs.length > 0 || conns.length > 0 || grps.length > 0 || paths.length > 0) return false
    if (floors.length !== 1) return false
    const f = floors[0]
    return f.id === 'f1' && f.name === 'F1 一层' && f.elevation === 0 && f.height === 5
  }

  function closeTab(id: string) {
    const idx = tabs.value.findIndex(t => t.id === id)
    if (idx < 0) return
    tabs.value.splice(idx, 1)
    if (activeTabId.value === id) {
      activeTabId.value = tabs.value[Math.max(0, idx - 1)]?.id ?? ''
    }
    if (tabs.value.length === 0) {
      const newTab = createTab(DEFAULT_SCENE_NAME)
      activeTabId.value = newTab.id
    }
    markDirty()
  }

  function renameTab(id: string, name: string) {
    const tab = tabs.value.find(t => t.id === id)
    if (tab) {
      tab.name = name
      if (tab.data.meta) tab.data.meta.name = name
      markDirty()
    }
  }

  // 对象操作（直接修改 objects 数组）
  function addObject(obj: SceneObject) {
    const scene = activeScene()
    if (!scene) return
    scene.objects.push(migrateLegacyPolylineObject(obj))
  }

  function removeObject(id: string) {
    const scene = activeScene()
    if (!scene) return
    scene.objects = scene.objects.filter(o => o.id !== id)
    scene.connections = (scene.connections ?? []).filter(c => c.fromId !== id && c.toId !== id)
    scene.groups = (scene.groups ?? [])
      .map(g => ({ ...g, memberIds: g.memberIds.filter(mid => mid !== id) }))
      .filter(g => g.memberIds.length > 0)
  }

  function updateObject(id: string, patch: Partial<SceneObject>) {
    const scene = activeScene()
    if (!scene) return
    const idx = scene.objects.findIndex(o => o.id === id)
    if (idx < 0) return
    scene.objects[idx] = migrateLegacyPolylineObject({ ...scene.objects[idx], ...patch })
  }

  function updateObjectParams(id: string, params: Record<string, any>) {
    const scene = activeScene()
    if (!scene) return
    const idx = scene.objects.findIndex(o => o.id === id)
    if (idx < 0) return
    const obj = scene.objects[idx]
    scene.objects[idx] = migrateLegacyPolylineObject({ ...obj, params: { ...obj.params, ...params } })
  }

  // 批量替换当前场景的 objects（用于撤销/重做）
  function setObjects(objects: SceneObject[]) {
    const scene = activeScene()
    if (!scene) return
    scene.objects = objects.map(migrateLegacyPolylineObject)
  }

  function getEditorSnapshot(): EditorSnapshot {
    return getTabSnapshot(activeTabId.value)
  }

  function applySnapshot(snapshot: EditorSnapshot) {
    const scene = activeScene()
    if (!scene) return
    scene.objects = [...snapshot.objects]
    scene.connections = [...snapshot.connections]
    scene.floors = [...snapshot.floors]
    scene.groups = [...snapshot.groups]
    scene.paths = [...snapshot.paths]
    if ('physicsConfig' in snapshot) {
      if (snapshot.physicsConfig === null) delete scene.physicsConfig
      else if (snapshot.physicsConfig !== undefined) {
        scene.physicsConfig = deepClone(snapshot.physicsConfig)
      }
    }
    // 旧撤销栈条目可能没有该字段：忽略，避免一按 Ctrl+Z 把现有源形态冲掉
    const legacySourceShape = (snapshot as EditorSnapshot & { sourceAdapterTemplate?: SourceShape | null })
      .sourceAdapterTemplate
    if ('sourceShape' in snapshot || legacySourceShape !== undefined) {
      const value = snapshot.sourceShape !== undefined ? snapshot.sourceShape : legacySourceShape
      if (value == null) delete scene.sourceShape
      else scene.sourceShape = deepClone(value)
    }
    // v2 撤销栈：数据服务列表（旧条目无此字段则忽略，不覆盖）
    if ('dataServices' in snapshot) {
      if (snapshot.dataServices == null) delete scene.dataServices
      else scene.dataServices = deepClone(snapshot.dataServices)
    }
  }

  // 加载模板（替换当前场景数据）
  function loadTemplate(data: SceneData) {
    clearPreviewLiveDataOverrides()
    const tab = tabs.value.find(t => t.id === activeTabId.value)
    if (!tab) return
    tab.data = {
      ...hydrateSceneData(cloneSceneData(data)),
      meta: { ...data.meta, id: uuidv4(), createdAt: new Date().toISOString() },
    }
    tab.name = tab.data.meta?.name || tab.name
    tab.lastSavedSnapshot = null
    tab.lastSavedSceneConfig = null
    tab.lastSavedSourceShape = null
  }

  /**
   * 保存到云端（docs/10 §2.3）：内容寻址去重 + 乐观锁。
   * - 显式保存（Ctrl+S / 保存按钮）直接调用（manual: true，成功提示短暂显示）；
   * - autosave 节流（App.vue）也调这里——保存成功后 markProjectSaved 重置 dirty 基准，
   *   失败（offline/conflict）只置 saveState，由下一次节流重试自然补传。
   * - 云项目名与 scene.json meta.name 不一致时，先同步 project 表名（docs/10 §2.9）。
   */
  async function save(opts?: { manual?: boolean }): Promise<void> {
    return enqueueSave(async () => {
      const platform = usePlatformStore()
      if (!platform.currentProject || !platform.tenant) return
      const { cloudSaveDraft } = await import('../platform/projectSync')
      const prevName = platform.currentProject.name
      const nextName = projectMeta.value.name.trim()
      if (nextName && nextName !== prevName) {
        const { apiUpdateProject } = await import('../platform/api')
        try {
          await apiUpdateProject(platform.tenant.publicId, platform.currentProject.publicId, { name: nextName })
          platform.currentProject = { ...platform.currentProject, name: nextName }
        } catch {
          /* 改名失败不阻断保存（列表名下次保存再试） */
        }
      }
      await cloudSaveDraft(platform, buildDraftObject(), { manual: opts?.manual })
      if (platform.saveState === 'saved') {
        markProjectSaved()
        touchLastSavedAt()
      }
    })
  }

  /** 最近一次成功保存的时刻（供 autosave 限流判断） */
  function lastSavedAtMs(): number {
    return lastSavedTimestampMs
  }
  let lastSavedTimestampMs = 0
  function touchLastSavedAt(): void {
    lastSavedTimestampMs = Date.now()
    const now = new Date()
    const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
    lastSavedTime.value = timeStr
  }

  // 导出当前场景 JSON
  function exportSceneJson(): string {
    return serialize(activeScene()!)
  }

  // 导入场景 JSON
  function importSceneJson(json: string) {
    clearPreviewLiveDataOverrides()
    const data = deserialize(json)
    replaceTabScene(activeTabId.value, data)
  }

  // 直接导入场景数据（避免打开文件时重复序列化）
  function importSceneData(data: SceneData) {
    clearPreviewLiveDataOverrides()
    replaceTabScene(activeTabId.value, data)
  }

  function replaceTabScene(id: string, data: SceneData, options?: { markSaved?: boolean }) {
    const tab = getTabById(id)
    if (!tab) return
    tab.data = hydrateSceneData(cloneSceneData(data))
    tab.name = data.meta?.name || tab.name
    tab.lastSavedSnapshot = options?.markSaved === false ? null : getTabSnapshot(id)
    tab.lastSavedSceneConfig = options?.markSaved === false ? null : cloneSceneConfig(tab.data.scene)
    tab.lastSavedSourceShape = options?.markSaved === false
      ? null
      : cloneSourceShape(tab.data.sourceShape)
    markDirty()
  }

  function openSceneAsNewTab(data: SceneData, options?: { markSaved?: boolean }) {
    const name = data.meta?.name || `场景 ${tabs.value.length + 1}`
    const tab: SceneTab = {
      id: uuidv4(),
      name,
      data: hydrateSceneData(cloneSceneData(data)),
      lastSavedSnapshot: null,
      lastSavedSceneConfig: null,
      lastSavedSourceShape: null,
    }
    tabs.value.push(tab)
    activeTabId.value = tab.id
    tab.lastSavedSnapshot = options?.markSaved === false ? null : getTabSnapshot(tab.id)
    tab.lastSavedSceneConfig = options?.markSaved === false ? null : cloneSceneConfig(tab.data.scene)
    tab.lastSavedSourceShape = options?.markSaved === false
      ? null
      : cloneSourceShape(tab.data.sourceShape)
    markDirty()
    return tab
  }

  function markTabSaved(id: string) {
    const tab = getTabById(id)
    if (!tab) return
    tab.lastSavedSnapshot = getTabSnapshot(id)
    tab.lastSavedSceneConfig = cloneSceneConfig(tab.data.scene)
    tab.lastSavedSourceShape = cloneSourceShape(tab.data.sourceShape)
  }

  /** 工程保存成功后调用：所有场景标记为已保存、记录连接配置基准（云端为唯一数据源，无文件引用） */
  function markProjectSaved() {
    for (const tab of tabs.value) markTabSaved(tab.id)
    lastSavedRealtimeConfig.value = { ...projectRealtimeConfig.value }
    lastSavedConnectConfig.value = { ...projectConnectConfig.value }
  }

  // ── 楼层操作 ──────────────────────────────────────────
  function activeFloors(): FloorDef[] {
    return activeScene()?.floors ?? [{ id: 'f1', name: 'F1 一层', elevation: 0, height: 5 }]
  }

  /**
   * 新增楼层。backfillFloorId：老场景/模板的对象从没写过 floorId，
   * isObjectOnCurrentFloor 对「没有 floorId」按「每层都显示」处理（渲染层兼容旧数据的默认值）——
   * 单层裂变为多层的这一刻，把这些对象一次性归到当前层，否则它们会在新增的空楼层上"复现"，看起来像是被复制了。
   *
   * 返回新楼层 id：新增成功后调用方要切到这一层，让用户直接落在刚建好的空楼层上。
   */
  function addFloor(name: string, height = 5, backfillFloorId?: string): string | null {
    const scene = activeScene()
    if (!scene) return null
    if (backfillFloorId) {
      scene.objects = scene.objects.map(o => (o.floorId ? o : { ...o, floorId: backfillFloorId }))
    }
    const existing = scene.floors ?? []
    const elevation = existing.reduce((sum, f) => sum + f.height, 0)
    const newFloor: FloorDef = { id: uuidv4(), name, elevation, height }
    scene.floors = [...existing, newFloor]
    return newFloor.id
  }

  /**
   * 删除楼层，连带删掉层上的对象。
   * 删掉中间层后必须重排剩余楼层的 elevation：否则上方楼层会悬在原来的高度留出一个空档，
   * 而 addFloor 的 elevation 按「各层高度累加」算，下次新增会算出与某个现存楼层相同的标高（两层叠在一起）。
   * elevation 同时是层上对象的世界 Y 基准，重排时把对象 Y 一起平移相同差值（与 copyFloorToFloor 同样的口径）。
   */
  function removeFloor(id: string) {
    const scene = activeScene()
    if (!scene || !scene.floors) return
    scene.floors = scene.floors.filter(f => f.id !== id)
    scene.objects = scene.objects.filter(o => o.floorId !== id)

    const deltaByFloor = new Map<string, number>()
    let elevation = 0
    scene.floors = scene.floors.map(f => {
      const delta = elevation - f.elevation
      if (delta !== 0) deltaByFloor.set(f.id, delta)
      const next: FloorDef = { ...f, elevation }
      elevation += f.height
      return next
    })
    if (!deltaByFloor.size) return
    scene.objects = scene.objects.map(o => {
      const delta = o.floorId ? deltaByFloor.get(o.floorId) : undefined
      if (!delta) return o
      return { ...o, position: [o.position[0], o.position[1] + delta, o.position[2]] as [number, number, number] }
    })
  }

  /**
   * 将源楼层上的所有对象克隆到目标楼层（世界坐标 Y 按楼层 elevation 差平移）。
   * v1：不复制连线；新对象清除 groupId；不复制组定义。
   */
  function copyFloorToFloor(sourceFloorId: string, targetFloorId: string) {
    const scene = activeScene()
    if (!scene) return
    const floors = scene.floors ?? []
    const srcF = floors.find(f => f.id === sourceFloorId)
    const tgtF = floors.find(f => f.id === targetFloorId)
    if (!srcF || !tgtF || sourceFloorId === targetFloorId) return

    const dy = tgtF.elevation - srcF.elevation
    const sources = scene.objects.filter(o => (o.floorId ?? 'f1') === sourceFloorId)
    for (const src of sources) {
      const pos: [number, number, number] = [src.position[0], src.position[1] + dy, src.position[2]]
      const newObj = cloneSceneObjectAt(src, pos, `${src.name}-层副本`)
      newObj.floorId = targetFloorId
      scene.objects.push(newObj)
    }
  }

  // ── 路径操作 ──────────────────────────────────────────
  function activePaths(): PathDef[] {
    return activePathsOf(activeScene())
  }

  function addPath(path: PathDef) {
    addPathToScene(activeScene(), path)
  }

  function removePath(id: string) {
    removePathFromScene(activeScene(), id)
  }

  function updatePath(id: string, patch: Partial<PathDef>) {
    updatePathInScene(activeScene(), id, patch)
  }

  // ── 连线操作 ──────────────────────────────────────────
  function activeConnections(): Connection[] {
    return activeScene()?.connections ?? []
  }

  function activeGroups(): ObjectGroup[] {
    return activeScene()?.groups ?? []
  }

  function addConnection(conn: Connection) {
    const scene = activeScene()
    if (!scene) return
    scene.connections = [...(scene.connections ?? []), conn]
  }

  function removeConnection(id: string) {
    const scene = activeScene()
    if (!scene) return
    scene.connections = (scene.connections ?? []).filter(c => c.id !== id)
  }

  function updateConnection(id: string, patch: Partial<Connection>) {
    const scene = activeScene()
    if (!scene) return
    const idx = (scene.connections ?? []).findIndex(c => c.id === id)
    if (idx < 0) return
    scene.connections![idx] = { ...scene.connections![idx], ...patch }
  }

  function createGroup(memberIds: string[], name?: string): string | null {
    const scene = activeScene()
    if (!scene) return null
    const uniqueIds = Array.from(new Set(memberIds))
    if (uniqueIds.length < 2) return null
    const groupId = uuidv4()
    const group: ObjectGroup = {
      id: groupId,
      name: name?.trim() || `分组 ${Math.max(1, (scene.groups ?? []).length + 1)}`,
      memberIds: uniqueIds
    }
    scene.groups = [...(scene.groups ?? []), group]
    scene.objects = scene.objects.map(obj => uniqueIds.includes(obj.id) ? { ...obj, groupId } : obj)
    return groupId
  }

  function ungroupByObjectIds(ids: string[]) {
    const scene = activeScene()
    if (!scene) return
    const idSet = new Set(ids)
    const groupIds = new Set(
      scene.objects
        .filter(o => idSet.has(o.id) && o.groupId)
        .map(o => o.groupId as string)
    )
    if (!groupIds.size) return
    scene.objects = scene.objects.map(obj => (
      obj.groupId && groupIds.has(obj.groupId) ? { ...obj, groupId: undefined } : obj
    ))
    scene.groups = (scene.groups ?? []).filter(g => !groupIds.has(g.id))
  }

  // ── 场景设置 ──────────────────────────────────────────
  function updateSceneConfig(patch: Record<string, any>) {
    const scene = activeScene()
    if (!scene) return
    scene.scene = { ...scene.scene, ...patch }
  }

  function updateLighting(patch: Record<string, any>) {
    const scene = activeScene()
    if (!scene) return
    scene.scene.lighting = { ...(scene.scene.lighting ?? DEFAULT_LIGHTING), ...patch }
  }

  /** 一键重置：恢复为「颜色正常」默认光照（保留阴影） */
  function resetLighting() {
    const scene = activeScene()
    if (!scene) return
    scene.scene.lighting = { ...DEFAULT_LIGHTING }
  }

  /** 一键去除：无阴影、高环境光，颜色更平、更接近设定色 */
  function setLightingFlat() {
    const scene = activeScene()
    if (!scene) return
    scene.scene.lighting = { ...FLAT_LIGHTING }
  }

  /**
   * 合并三处来源返回：连接字段（工程级共享）+ showBadge/preferPerformance（编辑器个人偏好）+ enabled（会话态）。
   * 必须是普通函数、每次调用都读取当下的 ref 值，不能缓存返回对象——
   * 否则 SceneCanvas 里 `watch(() => sceneStore.getRealtimeDebugConfig(), ...)` 在只改 showBadge 时追踪不到依赖。
   */
  function getRealtimeDebugConfig(): RealtimeDebugConfig {
    return {
      ...projectRealtimeConfig.value,
      enabled: debugEnabled.value,
      showBadge: editorPrefsStore.showBadge,
      preferPerformance: editorPrefsStore.preferPerformance,
    }
  }

  function updateRealtimeDebugConfig(patch: Partial<RealtimeDebugConfig>) {
    const { showBadge, preferPerformance, enabled, ...connectionPatch } = patch
    if (showBadge !== undefined) editorPrefsStore.setShowBadge(showBadge)
    if (preferPerformance !== undefined) editorPrefsStore.setPreferPerformance(preferPerformance)
    if (enabled !== undefined) debugEnabled.value = enabled
    if (Object.keys(connectionPatch).length > 0) {
      projectRealtimeConfig.value = { ...projectRealtimeConfig.value, ...connectionPatch }
      markDirty()
    }
  }

  function updateRuntimePolicies(patch: Partial<RuntimePoliciesConfig>) {
    const scene = activeScene()
    if (!scene) return
    const prev: Partial<RuntimePoliciesConfig> = scene.runtimePolicies ?? {}
    scene.runtimePolicies = {
      defaultRole: patch.defaultRole ?? prev.defaultRole ?? 'viewer',
      objectRoleOverrides:
        patch.objectRoleOverrides !== undefined
          ? { ...(prev.objectRoleOverrides ?? {}), ...patch.objectRoleOverrides }
          : { ...(prev.objectRoleOverrides ?? {}) },
      simulationPreferred:
        patch.simulationPreferred !== undefined ? patch.simulationPreferred : prev.simulationPreferred,
    }
  }

  function updateDataIntegration(patch: Partial<DataIntegrationPreviewConfig>) {
    const scene = activeScene()
    if (!scene) return
    scene.dataIntegration = { ...(scene.dataIntegration ?? {}), ...patch }
  }

  /** 源形态（不含密钥）；整份替换，避免半份 merge 丢字段。 */
  function setSourceShape(sourceShape: SourceShape | undefined) {
    const scene = activeScene()
    if (!scene) return
    if (sourceShape === undefined) {
      delete scene.sourceShape
    } else {
      scene.sourceShape = sourceShape
    }
    markDirty()
  }

  function getSourceShape(): SourceShape | undefined {
    return activeScene()?.sourceShape
  }

  // ── 工程级「项目连接」（线 G）──────────────────────────

  /** 工程级非密钥连接配置；patch 合并后写草稿。若地址/账号/组织变更且仍有 token，自动作废票据（须重新登录）。 */
  function updateProjectConnect(patch: Partial<ProjectConnectConfig>) {
    const prev = projectConnectConfig.value
    const next = { ...prev, ...patch }
    const hadToken = !!connectDataSecrets.value.token
    const profileChanged = hadToken && connectAuthProfileKey(prev) !== connectAuthProfileKey(next)
    projectConnectConfig.value = next
    if (profileChanged) {
      clearConnectDataToken(projectMeta.value.id)
      reloadConnectDataSecrets()
    }
    markDirty()
  }

  /** 本机 connectData 密钥（password/token/过期时间，按工程 id 分桶） */
  function getConnectSecrets(): ConnectDataSecrets {
    return { ...connectDataSecrets.value }
  }

  /** 仅更新本机密码（载入配置等）；不触碰 token */
  function patchConnectDataPassword(password: string) {
    if (!password) return
    saveConnectDataSecrets(projectMeta.value.id, { password })
    reloadConnectDataSecrets()
  }

  /**
   * 登录成功后落库：token/过期时间写本机 localSecrets，非密钥字段回写工程配置。
   * password 也一并存本机（「重新登录」可免输密码；不进 .f3p）。
   */
  function saveConnectDataLogin(session: RuntimeSessionAuth, password = '') {
    const cfg = { ...projectConnectConfig.value }
    if (session.baseUrl) cfg.baseUrl = session.baseUrl
    if (session.organizationId) cfg.organizationId = session.organizationId
    saveConnectDataSecrets(projectMeta.value.id, {
      password: password || undefined,
      token: session.token,
      expiresAtMs: session.expiresAtMs,
      refreshedAtMs: session.refreshedAtMs ?? Date.now(),
      authProfileKey: connectAuthProfileKey(cfg),
    })
    if (session.baseUrl) projectConnectConfig.value = { ...projectConnectConfig.value, baseUrl: session.baseUrl }
    if (session.organizationId) {
      projectConnectConfig.value = { ...projectConnectConfig.value, organizationId: session.organizationId }
    }
    reloadConnectDataSecrets()
    markDirty()
    // 登录成功后若 live 可用则自动切真实数据（D3 正解：无需用户再点任何按钮）
    if (canUseLiveData.value) setDataSourceMode('live')
  }

  /** 登出：清掉本机密钥；工程内非密钥字段保留（可重新登录再换 token） */
  function clearConnectDataLogin() {
    clearConnectDataSecrets(projectMeta.value.id)
    connectDataSecrets.value = {}
    if (getDataSourceMode() === 'live') setDataSourceMode('mock')
    markDirty()
  }

  /** 登录状态：token 未过期且与当前工程配置指纹一致 = 已登录 */
  const connectLoginStatus = computed((): ProjectConnectStatus => {
    const secrets = connectDataSecrets.value
    if (!secrets.token) return 'logged-out'
    if (
      secrets.authProfileKey
      && secrets.authProfileKey !== connectAuthProfileKey(projectConnectConfig.value)
    ) {
      return 'logged-out'
    }
    if (secrets.expiresAtMs != null && secrets.expiresAtMs > 0 && secrets.expiresAtMs <= Date.now()) {
      return 'expired'
    }
    return 'logged-in'
  })

  function getConnectStatus(): ProjectConnectStatus {
    return connectLoginStatus.value
  }

  /** 编辑器侧可用的运行会话（Hub 试拉 / 持续看数用本机 token，不再填密码） */
  function getConnectRuntimeSession(): RuntimeSessionAuth | null {
    if (getConnectStatus() !== 'logged-in') return null
    const secrets = connectDataSecrets.value
    const cfg = projectConnectConfig.value
    return {
      baseUrl: cfg.baseUrl,
      token: secrets.token!,
      expiresAtMs: secrets.expiresAtMs,
      organizationId: cfg.organizationId || undefined,
      refreshedAtMs: secrets.refreshedAtMs,
    }
  }

  function getProjectConnect(): ProjectConnectConfig {
    return { ...projectConnectConfig.value }
  }

  // ── 轴 A · 数据源（工程级唯一真值，docs/06-模式与数据源整合.md）──────

  /** 读取数据源模式：缺省等价于 mock（旧工程无此字段）。必须每次读当下的 ref，不能缓存。 */
  function getDataSourceMode(): DataSourceMode {
    return projectRealtimeConfig.value.dataSourceMode ?? 'mock'
  }

  /** live 可用：已登录 且 有数据服务 且 当前场景存在带 assetCode 的对象 */
  const canUseLiveData = computed(() => {
    if (connectLoginStatus.value !== 'logged-in') return false
    if (getDataServices().length === 0) return false
    return activeObjects().some((o) => !!o.assetCode)
  })

  /** 数据源唯一写入口；live 不可用时拒绝置为 live（UI 应已置灰，这里兜底）。返回是否生效。 */
  function setDataSourceMode(mode: DataSourceMode): boolean {
    if (mode === 'live' && !canUseLiveData.value) return false
    if (getDataSourceMode() === mode) return true
    projectRealtimeConfig.value = { ...projectRealtimeConfig.value, dataSourceMode: mode }
    markDirty()
    return true
  }

  /** 打开工程后：live 但当前不可用则降级 mock，不得静默假装在连 */
  function normalizeDataSourceMode() {
    if (getDataSourceMode() === 'live' && !canUseLiveData.value) {
      projectRealtimeConfig.value = { ...projectRealtimeConfig.value, dataSourceMode: 'mock' }
    }
  }

  // live 可用性变为 false 时（登出/删服务/解除最后绑定）自动降级，避免残留 live 态
  watch(canUseLiveData, (ok) => {
    if (!ok && getDataSourceMode() === 'live') {
      projectRealtimeConfig.value = { ...projectRealtimeConfig.value, dataSourceMode: 'mock' }
      markDirty()
    }
  })

  // ── 场景级数据服务（线 H）─────────────────────────────

  function getDataServices(): DataService[] {
    const scene = activeScene()
    return scene ? dataServicesOf(scene) : []
  }

  function upsertDataService(service: DataService) {
    const scene = activeScene()
    if (!scene) return
    sceneStoreApplySceneData(upsertDataServiceInScene(scene, service))
  }

  function removeDataService(serviceId: string) {
    const scene = activeScene()
    if (!scene) return
    sceneStoreApplySceneData(removeDataServiceFromScene(scene, serviceId))
  }

  /** 内存态整份替换当前场景数据（服务 CRUD 用；对象引用保持） */
  function sceneStoreApplySceneData(next: SceneData) {
    const tab = tabs.value.find((t) => t.id === activeTabId.value)
    if (!tab) return
    const prevData = tab.data
    tab.data = {
      ...next,
      objects: next.objects ?? prevData.objects ?? [],
      scene: next.scene ?? prevData.scene,
      meta: next.meta ?? prevData.meta,
      version: next.version ?? prevData.version,
    }
    markDirty()
  }

  function getPreviewLiveDataOverrides(): Record<string, unknown> {
    return previewLiveDataOverrides.value
  }

  function clearPreviewLiveDataOverrides() {
    previewLiveDataOverrides.value = {}
  }

  /** 按点路径写入预览覆盖（深拷贝后 setByPath） */
  function setPreviewLiveDataOverrideByPath(path: string, value: unknown) {
    const root = deepClone(previewLiveDataOverrides.value ?? {})
    setByPath(root, path, value)
    previewLiveDataOverrides.value = root
  }

  function applyDataBindingsSidecar(payload: {
    objects?: { id: string; dataBindings: ObjectDataBindings }[]
  }) {
    const scene = activeScene()
    if (!scene || !Array.isArray(payload.objects)) return
    const map = new Map(payload.objects.map((o) => [o.id, o.dataBindings]))
    scene.objects = scene.objects.map((obj) => {
      const db = map.get(obj.id)
      return db ? { ...obj, dataBindings: db } : obj
    })
  }

  function setSimulationMonitorMode(on: boolean) {
    simulationMonitorMode.value = on
    if (on) {
      dashboardComposeMode.value = false
      physicsEditSelection.value = null
    }
  }

  function setEditorLeftTab(tab: 'components' | 'sceneTree' | 'physics') {
    editorLeftTab.value = tab
  }

  function setDashboardComposeMode(on: boolean) {
    dashboardComposeMode.value = on
    if (on) {
      simulationMonitorMode.value = false
      physicsEditSelection.value = null
    }
    if (!on) resetDashboardComposeUi()
    else composeRightPanelTab.value = 'scene'
  }

  function ensurePhysicsConfig() {
    const scene = activeScene()
    if (!scene) return
    scene.physicsConfig ??= {
      version: 1,
      engine: 'external',
      zones: [],
      flowNetwork: { edges: [] },
      sources: [],
      bindings: [],
    }
  }

  function setPhysicsEditSelection(sel: PhysicsEditSelection | null) {
    physicsEditSelection.value = sel
    if (sel) composeRightPanelTab.value = 'physics'
  }

  function clearPhysicsEditSelection() {
    physicsEditSelection.value = null
  }

  function addPhysicsZone() {
    ensurePhysicsConfig()
    const scene = activeScene()
    if (!scene?.physicsConfig) return
    const id = `zone-${uuidv4().slice(0, 8)}`
    const n = scene.physicsConfig.zones.length + 1
    const z: PhysicsZone = {
      id,
      name: `新区域 ${n}`,
      volume: 100,
      initTempC: 20,
      initRh: 50,
    }
    scene.physicsConfig.zones.push(z)
    physicsEditSelection.value = { kind: 'zone', id }
    composeRightPanelTab.value = 'physics'
  }

  function addPhysicsFlowEdge() {
    ensurePhysicsConfig()
    const scene = activeScene()
    if (!scene?.physicsConfig) return
    const zones = scene.physicsConfig.zones
    const from = zones[0]?.id ?? 'zone-a'
    const to = zones[1]?.id ?? zones[0]?.id ?? 'zone-b'
    const id = `edge-${uuidv4().slice(0, 8)}`
    const edgeNum = scene.physicsConfig.flowNetwork.edges.length + 1
    const e: PhysicsFlowEdge = {
      id,
      name: `管段 ${edgeNum}`,
      from,
      to,
      medium: 'water',
      diameterM: 0.1,
      lengthM: 10,
    }
    scene.physicsConfig.flowNetwork.edges.push(e)
    physicsEditSelection.value = { kind: 'edge', id }
    composeRightPanelTab.value = 'physics'
  }

  function addPhysicsSource() {
    ensurePhysicsConfig()
    const scene = activeScene()
    if (!scene?.physicsConfig) return
    const id = `src-${uuidv4().slice(0, 8)}`
    const srcNum = scene.physicsConfig.sources.length + 1
    const s: PhysicsSource = {
      id,
      name: `源项 ${srcNum}`,
      type: 'heat',
      enabled: true,
    }
    scene.physicsConfig.sources.push(s)
    physicsEditSelection.value = { kind: 'source', id }
    composeRightPanelTab.value = 'physics'
  }

  function addPhysicsBinding() {
    ensurePhysicsConfig()
    const scene = activeScene()
    if (!scene?.physicsConfig) return
    const id = `bind-${uuidv4().slice(0, 8)}`
    const b: PhysicsBinding = {
      id,
      sourceObjectId: '',
      sourceMetric: 'tempC',
      targetType: 'zone',
      targetId: scene.physicsConfig.zones[0]?.id ?? '',
      targetMetric: 'tempC',
    }
    scene.physicsConfig.bindings.push(b)
    physicsEditSelection.value = { kind: 'binding', id }
    composeRightPanelTab.value = 'physics'
  }

  function updatePhysicsZone(id: string, patch: Partial<PhysicsZone>) {
    const scene = activeScene()
    const z = scene?.physicsConfig?.zones.find((x) => x.id === id)
    if (!z) return
    Object.assign(z, patch)
  }

  function updatePhysicsFlowEdge(id: string, patch: Partial<PhysicsFlowEdge>) {
    const scene = activeScene()
    const e = scene?.physicsConfig?.flowNetwork.edges.find((x) => x.id === id)
    if (!e) return
    Object.assign(e, patch)
  }

  function updatePhysicsSource(id: string, patch: Partial<PhysicsSource>) {
    const scene = activeScene()
    const s = scene?.physicsConfig?.sources.find((x) => x.id === id)
    if (!s) return
    Object.assign(s, patch)
  }

  function updatePhysicsBinding(id: string, patch: Partial<PhysicsBinding>) {
    const scene = activeScene()
    const b = scene?.physicsConfig?.bindings.find((x) => x.id === id)
    if (!b) return
    Object.assign(b, patch)
  }

  function removePhysicsEntity(kind: PhysicsEditSelection['kind'], id: string) {
    const scene = activeScene()
    const pc = scene?.physicsConfig
    if (!pc) return
    if (kind === 'zone') pc.zones = pc.zones.filter((z) => z.id !== id)
    else if (kind === 'edge') pc.flowNetwork.edges = pc.flowNetwork.edges.filter((e) => e.id !== id)
    else if (kind === 'source') pc.sources = pc.sources.filter((s) => s.id !== id)
    else if (kind === 'binding') pc.bindings = pc.bindings.filter((b) => b.id !== id)
    if (physicsEditSelection.value?.id === id) physicsEditSelection.value = null
  }

  function setComposeSelectedWidgetId(id: string | null) {
    composeSelectedWidgetId.value = id
    if (id) composeRightPanelTab.value = 'widget'
  }

  function setComposeRightPanelTab(tab: 'scene' | 'physics' | 'widget') {
    composeRightPanelTab.value = tab
  }

  function toggleRightPanelCollapsed() {
    rightPanelCollapsed.value = !rightPanelCollapsed.value
  }

  // 切到某个 Tab（无论是手动点击，还是选中对象/控件时代码内部触发）都代表用户想看它，自动展开
  watch(composeRightPanelTab, () => {
    rightPanelCollapsed.value = false
  })

  function setEditorPreviewMergedLiveData(data: Record<string, unknown>) {
    editorPreviewMergedLiveData.value = data
  }

  function rememberLiveDataSeed(data: Record<string, unknown>) {
    if (!data || typeof data !== 'object' || Object.keys(data).length === 0) return
    lastLiveDataSeed.value = deepClone(data)
  }

  function getLastLiveDataSeed(): Record<string, unknown> {
    return lastLiveDataSeed.value
  }

  function clearLastLiveDataSeed() {
    lastLiveDataSeed.value = {}
  }

  return {
    tabs,
    activeTabId,
    lastSavedTime,
    dirtySignal,
    markDirty,
    projectMeta,
    projectRealtimeConfig,
    projectFileRef,
    activeScene,
    activeObjects,
    activeFloors,
    activeConnections,
    activeGroups,
    activePaths,
    loadCloudProject,
    forceSaveToCloud,
    loadProject,
    startFreshProject,
    createTab,
    switchTab,
    isTabDirty,
    isTabUnchanged,
    isProjectDirty,
    markTabSaved,
    markProjectSaved,
    replaceTabScene,
    openSceneAsNewTab,
    closeTab,
    renameProject,
    syncProjectName,
    renameTab,
    addObject,
    removeObject,
    updateObject,
    updateObjectParams,
    setObjects,
    getEditorSnapshot,
    applySnapshot,
    loadTemplate,
    save,
    lastSavedAtMs,
    touchLastSavedAt,
    exportSceneJson,
    importSceneJson,
    importSceneData,
    addFloor,
    removeFloor,
    copyFloorToFloor,
    addPath,
    removePath,
    updatePath,
    addConnection,
    removeConnection,
    updateConnection,
    createGroup,
    ungroupByObjectIds,
    updateSceneConfig,
    updateLighting,
    resetLighting,
    setLightingFlat,
    getRealtimeDebugConfig,
    updateRealtimeDebugConfig,
    updateRuntimePolicies,
    updateDataIntegration,
    setSourceShape,
    getSourceShape,
    projectConnectConfig,
    lastSavedConnectConfig,
    updateProjectConnect,
    getProjectConnect,
    getConnectSecrets,
    patchConnectDataPassword,
    saveConnectDataLogin,
    clearConnectDataLogin,
    connectLoginStatus,
    getConnectStatus,
    getConnectRuntimeSession,
    getDataSourceMode,
    setDataSourceMode,
    canUseLiveData,
    getDataServices,
    upsertDataService,
    removeDataService,
    previewLiveDataOverrides,
    getPreviewLiveDataOverrides,
    clearPreviewLiveDataOverrides,
    setPreviewLiveDataOverrideByPath,
    applyDataBindingsSidecar,
    simulationMonitorMode,
    setSimulationMonitorMode,
    editorLeftTab,
    setEditorLeftTab,
    ensurePhysicsConfig,
    physicsEditSelection,
    setPhysicsEditSelection,
    clearPhysicsEditSelection,
    addPhysicsZone,
    addPhysicsFlowEdge,
    addPhysicsSource,
    addPhysicsBinding,
    updatePhysicsZone,
    updatePhysicsFlowEdge,
    updatePhysicsSource,
    updatePhysicsBinding,
    removePhysicsEntity,
    dashboardComposeMode,
    setDashboardComposeMode,
    composeSelectedWidgetId,
    composeRightPanelTab,
    rightPanelCollapsed,
    toggleRightPanelCollapsed,
    composeGridSize,
    composeSnapToGrid,
    setComposeSnapToGrid,
    composeAutoResolvedFontPx,
    composeAutoResolvedLabelFontPx,
    setComposeSelectedWidgetId,
    setComposeRightPanelTab,
    setComposeGridSize,
    setComposeAutoResolvedFontPx,
    setComposeAutoResolvedLabelFontPx,
    editorPreviewMergedLiveData,
    setEditorPreviewMergedLiveData,
    lastLiveDataSeed,
    rememberLiveDataSeed,
    getLastLiveDataSeed,
    clearLastLiveDataSeed,
  }
})

export { useSelectionStore } from './selection'
